import { Close, FilterList, MailOutlineOutlined, MoreHoriz, Search } from "@mui/icons-material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import {
  Autocomplete,
  Badge,
  Button,
  Checkbox,
  IconButton,
  InputAdornment,
  ListItemText,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import { useMutation, useQuery } from "@tanstack/react-query";
import axios from "axios";
import DisplayDate from "components/DisplayDate";
import DownloadFile from "components/DownloadFile";
import { RefreshData } from "components/helper";
import DeleteModal from "components/Modals/DeleteModal";
import WholeScreenLoader from "components/WholeScreenLoader";
import { deleteData } from "const/axios";
import { API_URL } from "const/constants";
import { P } from "const/permissions";
import useKothar from "context/useKothar";
import { differenceInDays } from "date-fns";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { FaPlusCircle, FaTimes } from "react-icons/fa";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  DEFAULT_CLIENT_SORT_BY,
  DEFAULT_CLIENT_SORT_DIRECTION,
  months,
  progressStageOptions,
  reminderFilterOptions,
  sortByOptions,
  sortOrderOptions,
  visaExpiringDaysOptions,
} from "utils/clientService";
import { hasPermission, isSuperAdmin } from "utils/permissionUtils";
import TaskModal from "../Tasks/TaskModal";
import { ClientActionsMenu } from "./ClientListResponsive";
import ImportModal from "./ImportModal";
import PreivewModal from "./PreivewModal";
import VisaExpiringStudentsModal from "./VisaExpiringStudentModal";
import ClientDetailsShareModal from "components/Modals/ClientDetailsShareModal";

const filterFieldSx = {
  minWidth: { xs: "100%", sm: 140 },
  flex: { xs: "1 1 100%", sm: "0 1 auto" },
  maxWidth: { xs: "100%", lg: 220 },
};

const Clients = ({ color = "light" }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isCompact = useMediaQuery(theme.breakpoints.down("lg"));
  const [openConfirmationModal, setOpenConfirmationModal] = useState({});
  const [permanentDelete, setPermanentDelete] = useState(false);
  const [deletePreview, setDeletePreview] = useState(null);
  const [deletePreviewLoading, setDeletePreviewLoading] = useState(false);
  const [headerMoreAnchor, setHeaderMoreAnchor] = useState(null);
  const [
    { token, paginatedClientList, isPaginatedClientLoading, usersList, pagination, userDetail },
    { refetchPaginatedClient, setPagination, refetchUsers },
  ] = useKothar();

  const activeOrgId = userDetail?.activeOrgId ?? userDetail?.orgId;
  const orgUsers = usersList?.filter((user) => user?.organization?.id === activeOrgId);
  const caseOfficers = [...(orgUsers || []), { value: "NONE", id: null, name: "No Case Officer" }];
  const isAdminOrSuperAdmin = userDetail?.type === "SUPER_ADMIN" || userDetail?.type === "ADMIN";
  const isSuperAdminUser = isSuperAdmin();

  const [isAssignModalOpen, setIsAssignModelOpen] = useState({
    state: false,
    data: null,
  });
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [openVisaExpiringStudentsModal, setOpenVisaExpiringStudentsModal] = useState(false);
  const [sharePrForm, setSharePrForm] = useState(false);
  const [filterMenuAnchor, setFilterMenuAnchor] = useState(null);
  const [visibleFilters, setVisibleFilters] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("clientsVisibleFilters"));
      if (Array.isArray(saved)) {
        return new Set(saved.filter((key) => key !== "sortBy" && key !== "sortDirection"));
      }
    } catch {
      /* ignore */
    }
    return new Set(["caseOfficer", "progressStage"]);
  });
  // const [pagination, setPagination] = useState({
  //   pageIndex: 0,
  //   pageSize: 20,
  // });

  // Generate years array (current year and 5 years back)
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 6 }, (_, i) => currentYear - i);

  // Get unique case officers from the data

  // Filter function
  const handleFilterChange = (filterType, value) => {
    switch (filterType) {
      case "caseOfficer":
        setPagination((prev) => ({
          ...prev,
          caseOfficer: value === "No Case Officer" ? "NONE" : value?.slice(0, 4),
          pageIndex: 0,
        }));
        break;
      case "month":
        setPagination((prev) => ({ ...prev, createdMonth: value, pageIndex: 0 }));
        break;
      case "year":
        setPagination((prev) => ({ ...prev, createdYear: value, pageIndex: 0 }));
        break;
      case "progressStage":
        setPagination((prev) => ({ ...prev, progressStage: value, pageIndex: 0 }));
        break;
      case "sortBy":
        setPagination((prev) => ({
          ...prev,
          sortBy: value || DEFAULT_CLIENT_SORT_BY,
          pageIndex: 0,
        }));
        break;
      case "sortDirection":
        setPagination((prev) => ({
          ...prev,
          sortDirection: value || DEFAULT_CLIENT_SORT_DIRECTION,
          pageIndex: 0,
        }));
        break;
      default:
        break;
    }
  };

  const clearSingleFilter = (key) => {
    if (key === "hasReminders") {
      setPagination((prev) => ({
        ...prev,
        pageIndex: 0,
        hasReminders: undefined,
        reminderStatus: undefined,
      }));
    } else if (key === "visaExpiringDays") {
      setPagination((prev) => ({
        ...prev,
        pageIndex: 0,
        visaExpiringDays: "",
        visaExpiringFrom: "",
        visaExpiringTo: "",
      }));
    } else if (key === "sortBy") {
      setPagination((prev) => ({ ...prev, pageIndex: 0, sortBy: DEFAULT_CLIENT_SORT_BY }));
    } else if (key === "sortDirection") {
      setPagination((prev) => ({
        ...prev,
        pageIndex: 0,
        sortDirection: DEFAULT_CLIENT_SORT_DIRECTION,
      }));
    } else {
      setPagination((prev) => ({
        ...prev,
        pageIndex: 0,
        [key]: "",
      }));
    }
  };

  const clearFilters = () => {
    setPagination((prev) => ({
      ...prev,
      pageIndex: 0,
      searchText: "",
      caseOfficer: "",
      createdMonth: "",
      createdYear: "",
      progressStage: "",
      sortBy: DEFAULT_CLIENT_SORT_BY,
      sortDirection: DEFAULT_CLIENT_SORT_DIRECTION,
      hasReminders: undefined,
      reminderStatus: undefined,
      visaExpiringDays: "",
      visaExpiringFrom: "",
      visaExpiringTo: "",
    }));
  };

  useEffect(() => {
    if (!usersList?.length) refetchUsers?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const known = sortByOptions.some((option) => option.value === pagination?.sortBy);
    if (known) return;
    setPagination((prev) => ({
      ...prev,
      sortBy: DEFAULT_CLIENT_SORT_BY,
      sortDirection: DEFAULT_CLIENT_SORT_DIRECTION,
      pageIndex: 0,
    }));
  }, [pagination?.sortBy, setPagination]);

  const skippedCachedMountFetch = useRef(false);
  useEffect(() => {
    if (!skippedCachedMountFetch.current) {
      skippedCachedMountFetch.current = true;
      // Drop visa-expiring filters left by Dashboard so Clients opens unfiltered.
      if (
        pagination?.visaExpiringDays ||
        pagination?.visaExpiringFrom ||
        pagination?.visaExpiringTo ||
        pagination?.caseOfficer
      ) {
        refetchPaginatedClient({
          visaExpiringDays: "",
          visaExpiringFrom: "",
          visaExpiringTo: "",
          pageIndex: 0,
          caseOfficer: "",
        });
        return;
      }
      if (paginatedClientList?.content?.length) return;
    }
    const handle = setTimeout(() => {
      refetchPaginatedClient();
    }, 300);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    pagination.pageIndex,
    pagination.pageSize,
    pagination.searchText,
    pagination.caseOfficer,
    pagination.createdMonth,
    pagination.createdYear,
    pagination.progressStage,
    pagination.sortBy,
    pagination.sortDirection,
    pagination.hasReminders,
    pagination.reminderStatus,
    pagination.visaExpiringDays,
    pagination.visaExpiringFrom,
    pagination.visaExpiringTo,
  ]);

  const handleDeleteClient = () => {
    deleteData(
      `${API_URL}/clients/${openConfirmationModal?.id}`,
      () => {
        setOpenConfirmationModal({ state: false, id: null });
        refetchPaginatedClient({
          caseOfficer: pagination?.caseOfficer || "",
          createdMonth: pagination?.createdMonth || "",
          createdYear: pagination?.createdYear || "",
          progressStage: pagination?.progressStage || "",
          page: pagination.pageIndex,
          size: pagination.pageSize,
        });
      },
      token
    );
  };

  const fetchDeletePreview = (clientId) => {
    setDeletePreviewLoading(true);
    axios
      .get(`${API_URL}/clients/${clientId}/delete-preview`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => setDeletePreview(res.data))
      .catch(() => toast.error("Error loading delete preview"))
      .finally(() => setDeletePreviewLoading(false));
  };

  const handlePermanentDeleteToggle = (checked) => {
    if (checked && !isSuperAdminUser) {
      toast.error("Only a super admin can permanently delete a client");
      return;
    }
    setPermanentDelete(checked);
    if (checked && openConfirmationModal?.id) {
      fetchDeletePreview(openConfirmationModal.id);
    } else {
      setDeletePreview(null);
    }
  };

  const handlePermanentDeleteClient = () => {
    if (!isSuperAdminUser) {
      toast.error("Only a super admin can permanently delete a client");
      return;
    }
    axios({
      url: `${API_URL}/clients/${openConfirmationModal?.id}/permanent`,
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    })
      .then((res) => {
        if (res.data?.success === false) {
          toast.error(res.data?.message || "Error deleting client");
        } else {
          toast.success("Client permanently deleted");
          setOpenConfirmationModal({ state: false, id: null });
          setPermanentDelete(false);
          setDeletePreview(null);
          refetchPaginatedClient({
            caseOfficer: pagination?.caseOfficer || "",
            createdMonth: pagination?.createdMonth || "",
            createdYear: pagination?.createdYear || "",
            progressStage: pagination?.progressStage || "",
            page: pagination.pageIndex,
            size: pagination.pageSize,
          });
        }
      })
      .catch(() => {
        toast.error("Error deleting client");
      });
  };
  const [loading, setLoading] = useState(false);

  const previewCSVData = async () => {
    const res = await axios.get(`${API_URL}/clients/preview`);
    return res?.data?.data?.reverse();
  };

  const {
    data: previewData,
    refetch: refetchPreviewData,
    isFetching: isPreviewFetching,
  } = useQuery(["previewData"], previewCSVData, {
    refetchOnWindowFocus: false,
    enabled: false,
    onSuccess: (data) => {
      setPreviewModalOpen(true);
    },
    onError: (error) => {
      toast.error("Error fetching preview data");
    },
  });

  const importSelectedClientData = () => {
    setImportModalOpen(true);
  };

  const postSelectedClientData = async (data) => {
    const res = await axios.post(`${API_URL}/clients/import-selected`, data);
    return res?.data?.data;
  };

  const { mutate: postSelectedClientDataMutation } = useMutation(postSelectedClientData, {
    onSuccess: (data) => {
      toast.success("Data successfully imported");
      refetchPaginatedClient({
        page: pagination.pageIndex,
        size: pagination.pageSize,
        sortBy: pagination.sortBy,
        sortDirection: pagination.sortDirection,
      });
      setPreviewModalOpen(false);
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const fetchSelectedClientData = (data) => {
    postSelectedClientDataMutation({ clientData: data });
  };

  const fetchCSVData = () => {
    setLoading(true);
    const csvUrl =
      "https://docs.google.com/spreadsheets/d/e/2PACX-1vSRdcg10KGkr4GlYMzfVBvCT6C6tftxgTvnvjQztoi8EQxCseiIEfvElVQfBlZ6umsHYq37Q13NIeDh/pub?output=csv"; // Replace with your Google Sheets CSV file URL
    const apiUrl = `${API_URL}/clients/import`;
    axios
      .get(apiUrl) // Use Axios to fetch the CSV data
      .then((response) => {
        refetchPaginatedClient({
          page: pagination.pageIndex,
          size: pagination.pageSize,
          sortBy: pagination.sortBy,
          sortDirection: pagination.sortDirection,
        });
        setLoading(false);
        toast.success("Data successfully fetched from Google Sheets");
      })
      .catch((error) => {
        console.error("Error fetching CSV data:", error);
        setLoading(false);
        toast.error("Error importing data");
      });
  };

  const handleImportAll = async () => {
    fetchCSVData();
  };

  const handleImportSelected = async () => {
    await refetchPreviewData();
  };

  const handleImportUpdated = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/clients/import`);
      refetchPaginatedClient({
        page: pagination.pageIndex,
        size: pagination.pageSize,
        sortBy: pagination.sortBy,
        sortDirection: pagination.sortDirection,
      });
      toast.success("Updated data successfully imported");
    } catch (error) {
      console.error("Error importing updated data:", error);
      toast.error("Error importing updated data");
    } finally {
      setLoading(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "sn",
        header: "SN",
        size: 20,
        Cell: ({ row }) => {
          return <div>{row?.index + 1}</div>;
        },
      },
      {
        accessorKey: "name",
        header: "Name",
        size: 150,
        Cell: ({ row }) => {
          return (
            <Tooltip title={row?.original?.name}>
              <div className="truncate max-w-[200px]">{row?.original?.name || "-"}</div>
            </Tooltip>
          );
        },
      },
      {
        accessorKey: "email",
        header: "Email",
        size: 150,
      },
      {
        accessorKey: "number",
        header: "Number",
        size: 100,
      },
      {
        accessorKey: "passportNumber",
        header: "Passport Number",
        size: 100,
      },

      {
        accessorKey: "visaStatus",
        header: "Visa Status",
        size: 150,
      },
      {
        accessorKey: "visaExpiry",
        header: "Visa Expiry",
        size: 150,
        Cell: ({ row }) => {
          const daysUntilExpiry = differenceInDays(new Date(row?.original?.visaExpiry), new Date());
          const isExpiringSoon = daysUntilExpiry < 90;

          return (
            <>
              {row?.original?.visaExpiry ? (
                <div>
                  {" "}
                  <Tooltip
                    title={
                      isExpiringSoon
                        ? daysUntilExpiry > 0
                          ? `Visa expiring in ${daysUntilExpiry} days`
                          : "Visa expired, please contact Client"
                        : ""
                    }
                    arrow
                  >
                    <span
                      className={
                        " " +
                        (isExpiringSoon
                          ? "bg-red-400 text-white font-medium rounded-xl px-2 py-1"
                          : "")
                      }
                    >
                      <DisplayDate value={row?.original?.visaExpiry} fallback="-" />
                    </span>
                  </Tooltip>{" "}
                  {isExpiringSoon && (
                    <Tooltip title={"Send Email to the Client"} arrow>
                      <IconButton
                        onClick={() => {
                          const mailtoLink = `https://mail.google.com/mail/?view=cm&fs=1&to=${row?.original?.email}`;
                          window.open(mailtoLink, "_blank");
                        }}
                      >
                        <MailOutlineOutlined className="text-primary" />
                      </IconButton>
                    </Tooltip>
                  )}
                </div>
              ) : (
                "-"
              )}
            </>
          );
        },
      },
      {
        accessorKey: "caseOfficer",
        header: "Case Officer",
        size: 150,
      },
      {
        accessorKey: "isConverted",
        header: "Converted",
        size: 150,
        Cell: ({ row }) => {
          return (
            <div>
              {row?.original?.isConverted ? (
                <label className="bg-green-50 px-2 py-1 rounded-full  text-green-500">Yes</label>
              ) : (
                <label className="bg-red-50 px-2 py-1 rounded-full text-red-500">No</label>
              )}
            </div>
          );
        },
      },
      {
        accessorKey: "createdDate",
        header: "Created At",
        size: 150,
        Cell: ({ row }) => {
          return (
            <div>
              <DisplayDate value={row?.original?.createdDate} />
            </div>
          );
        },
      },
      {
        accessorKey: "actions",
        header: "Actions",
        size: 80,
        enableColumnPinning: true,
        Cell: ({ row }) => {
          const item = row.original;
          return (
            <ClientActionsMenu
              item={item}
              onAssignTask={(client) => setIsAssignModelOpen({ state: true, data: client })}
              onDelete={(client) => setOpenConfirmationModal({ state: true, id: client?.id })}
            />
          );
        },
      },
    ],
    []
  );

  const activeFilterChips = useMemo(() => {
    const chips = [];
    const officer =
      pagination?.caseOfficer === "NONE"
        ? "No Case Officer"
        : caseOfficers?.find((o) => o?.name?.slice(0, 4) === pagination?.caseOfficer)?.name || "";
    const monthLabel = months.find((m) => m.value === pagination?.createdMonth)?.label || "";
    const progressLabel =
      progressStageOptions.find((p) => p.value === pagination?.progressStage)?.label || "";
    const sortByLabel = sortByOptions.find((s) => s.value === pagination?.sortBy)?.label || "";
    const sortDirectionLabel =
      sortOrderOptions.find((s) => s.value === pagination?.sortDirection)?.label || "";

    if (officer) chips.push({ key: "caseOfficer", label: `Case Officer: ${officer}` });
    if (progressLabel) chips.push({ key: "progressStage", label: `Progress: ${progressLabel}` });
    if (monthLabel) chips.push({ key: "createdMonth", label: `Month: ${monthLabel}` });
    if (pagination?.createdYear)
      chips.push({ key: "createdYear", label: `Year: ${pagination.createdYear}` });
    if (pagination?.sortBy && pagination.sortBy !== DEFAULT_CLIENT_SORT_BY && sortByLabel) {
      chips.push({ key: "sortBy", label: `Sort By: ${sortByLabel}` });
    }
    if (
      pagination?.sortDirection &&
      pagination.sortDirection !== DEFAULT_CLIENT_SORT_DIRECTION &&
      sortDirectionLabel
    ) {
      chips.push({ key: "sortDirection", label: `Order: ${sortDirectionLabel}` });
    }
    if (pagination?.searchText)
      chips.push({ key: "searchText", label: `Search: ${pagination.searchText}` });
    if (pagination?.hasReminders) {
      const reminderLabel =
        reminderFilterOptions.find((o) => {
          if (pagination.reminderStatus === "pending") return o.value === "pending";
          if (pagination.reminderStatus === "completed") return o.value === "completed";
          return o.value === "has_all";
        })?.label || "All";
      chips.push({ key: "hasReminders", label: `Reminders: ${reminderLabel}` });
    }
    if (pagination?.visaExpiringDays) {
      const daysLabel =
        visaExpiringDaysOptions.find((o) => String(o.value) === String(pagination.visaExpiringDays))
          ?.label || `${pagination.visaExpiringDays} days`;
      chips.push({ key: "visaExpiringDays", label: `Visa Expiring: ${daysLabel}` });
    }

    return chips;
  }, [pagination, caseOfficers]);

  useEffect(() => {
    localStorage.setItem("clientsVisibleFilters", JSON.stringify([...visibleFilters]));
  }, [visibleFilters]);

  const toggleFilter = (key) => {
    setVisibleFilters((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
        // Clear the value when hiding so a hidden filter never silently filters.
        clearSingleFilter(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  // Each filter described once: how to toggle, render, and whether it's available.
  const filterConfigs = [
    {
      key: "caseOfficer",
      label: "Case Officer",
      adminOnly: true,
      render: () => (
        <Autocomplete
          key="caseOfficer"
          options={caseOfficers}
          getOptionLabel={(option) => option?.name || ""}
          value={
            pagination?.caseOfficer === "NONE"
              ? { value: "NONE", id: null, name: "No Case Officer" }
              : caseOfficers?.find(
                  (option) => option?.name?.slice(0, 4) === pagination?.caseOfficer
                ) || null
          }
          onChange={(event, newValue) => {
            handleFilterChange("caseOfficer", newValue ? newValue?.name : "");
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Case Officer"
              size="small"
              placeholder="All Case Officers"
            />
          )}
          sx={filterFieldSx}
        />
      ),
    },
    {
      key: "progressStage",
      label: "Progress",
      render: () => (
        <Autocomplete
          key="progressStage"
          options={progressStageOptions}
          getOptionLabel={(option) => option?.label || ""}
          value={progressStageOptions.find((o) => o.value === pagination?.progressStage) || null}
          onChange={(event, newValue) => {
            handleFilterChange("progressStage", newValue ? newValue.value : "");
          }}
          renderInput={(params) => (
            <TextField {...params} label="Progress" size="small" placeholder="All Stages" />
          )}
          sx={filterFieldSx}
        />
      ),
    },
    {
      key: "createdMonth",
      label: "Month",
      render: () => (
        <Autocomplete
          key="createdMonth"
          options={months}
          getOptionLabel={(option) => option.label || ""}
          value={months.find((option) => option.value === pagination?.createdMonth) || null}
          onChange={(event, newValue) => {
            handleFilterChange("month", newValue ? newValue.value : "");
          }}
          renderInput={(params) => (
            <TextField {...params} label="Month" size="small" placeholder="All Months" />
          )}
          sx={filterFieldSx}
        />
      ),
    },
    {
      key: "createdYear",
      label: "Year",
      render: () => (
        <Autocomplete
          key="createdYear"
          options={years.map((year) => ({ value: year, label: year.toString() }))}
          getOptionLabel={(option) => option.label || ""}
          value={
            years.find((year) => year === pagination?.createdYear)
              ? { value: pagination?.createdYear, label: pagination?.createdYear.toString() }
              : null
          }
          onChange={(event, newValue) => {
            handleFilterChange("year", newValue ? newValue.value : "");
          }}
          renderInput={(params) => (
            <TextField {...params} label="Year" size="small" placeholder="All Years" />
          )}
          sx={filterFieldSx}
        />
      ),
    },
    {
      key: "hasReminders",
      label: "Reminders",
      render: () => (
        <Autocomplete
          key="hasReminders"
          options={reminderFilterOptions}
          getOptionLabel={(option) => option.label || ""}
          value={
            pagination?.hasReminders
              ? reminderFilterOptions.find((o) => {
                  if (pagination.reminderStatus === "pending") return o.value === "pending";
                  if (pagination.reminderStatus === "completed") return o.value === "completed";
                  return o.value === "has_all";
                }) || null
              : null
          }
          onChange={(event, newValue) => {
            if (!newValue) {
              setPagination((prev) => ({
                ...prev,
                hasReminders: undefined,
                reminderStatus: undefined,
              }));
            } else {
              setPagination((prev) => ({
                ...prev,
                hasReminders: true,
                reminderStatus:
                  newValue.value === "pending" || newValue.value === "completed"
                    ? newValue.value
                    : undefined,
              }));
            }
          }}
          renderInput={(params) => (
            <TextField {...params} label="Reminders" size="small" placeholder="All Clients" />
          )}
          sx={filterFieldSx}
        />
      ),
    },
    {
      key: "visaExpiringDays",
      label: "Visa Expiring",
      render: () => (
        <Autocomplete
          key="visaExpiringDays"
          options={visaExpiringDaysOptions}
          getOptionLabel={(option) => option.label || ""}
          value={
            visaExpiringDaysOptions.find(
              (o) => String(o.value) === String(pagination?.visaExpiringDays)
            ) || null
          }
          onChange={(event, newValue) => {
            setPagination((prev) => ({
              ...prev,
              pageIndex: 0,
              visaExpiringDays: newValue ? newValue.value : "",
              visaExpiringFrom: "",
              visaExpiringTo: "",
            }));
          }}
          renderInput={(params) => (
            <TextField {...params} label="Visa Expiring" size="small" placeholder="Any expiry" />
          )}
          sx={filterFieldSx}
        />
      ),
    },
  ];

  const availableFilters = filterConfigs.filter((f) => !f.adminOnly || isAdminOrSuperAdmin);

  const table = useMaterialReactTable({
    columns,
    data: paginatedClientList?.content || [],
    rowNumberDisplayMode: "original",
    enableGlobalFilter: false,
    enableSorting: false,
    enableStickyHeader: true,
    enableColumnPinning: true,
    enableTopToolbar: false,
    manualPagination: true,
    manualFiltering: true,
    enablePagination: true,
    onPaginationChange: setPagination,
    initialState: {
      density: "compact",
      pagination: {
        pageSize: 20,
        pageIndex: 0,
      },
      columnPinning: {
        left: ["sn", "name", "email", "number"],
      },
    },
    muiPaginationProps: {
      rowsPerPageOptions: [20, 50, 100, 200, 400],
      showFirstButton: !isCompact,
      showLastButton: !isCompact,
      sx: {
        flexWrap: "wrap",
        justifyContent: { xs: "center", sm: "flex-end" },
        ".MuiTablePagination-toolbar": {
          flexWrap: "wrap",
          gap: 0.5,
          px: { xs: 0.5, sm: 2 },
        },
        ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows": {
          marginBottom: 0,
        },
      },
    },
    paginationDisplayMode: "pages",
    rowCount: paginatedClientList?.totalItems,
    pageCount: Math.ceil(paginatedClientList?.totalPages || 0),
    state: {
      pagination,
      isLoading: isPaginatedClientLoading,
      showProgressBars: isPaginatedClientLoading,
      isSaving: isPaginatedClientLoading,
      columnPinning: {
        left: isCompact ? null : ["sn", "name"],
        right: isCompact ? null : ["actions"],
      },
    },
    muiTablePaperProps: {
      sx: {
        boxShadow: "none",
        width: "100%",
      },
    },
    muiTableContainerProps: {
      sx: {
        maxHeight: { xs: "calc(100dvh - 260px)", lg: "calc(100vh - 300px)" },
        minHeight: { xs: 240, lg: "calc(100vh - 300px)" },
        overflowX: "auto",
        overflowY: "auto",
        WebkitOverflowScrolling: "touch",
        position: "relative",
      },
    },
    muiTableProps: {
      sx: {
        minWidth: 900,
      },
    },
    muiTableBodyRowProps: ({ row }) => ({
      onClick: (event) => {
        const isActionClick =
          event.target.closest(".MuiIconButton-root") || event.target.closest(".MuiPopover-root");
        if (!isActionClick) {
          navigate(`/admin/client/view/${row.original?.id}`, {
            state: { item: row.original },
          });
        }
      },
      sx: {
        cursor: "pointer",
        "&:hover": {
          backgroundColor: "rgba(0, 0, 0, 0.04)",
        },
      },
    }),
  });

  const headerSecondaryActions = [
    isAdminOrSuperAdmin && {
      key: "check-in",
      label: "Check In / Client Data Form",
      onClick: () => navigate("/admin/client/check-in"),
    },
    isAdminOrSuperAdmin && {
      key: "pr-enquiries",
      label: "PR Enquiries",
      onClick: () => navigate("/admin/client/pr-enquiries"),
    },

    isAdminOrSuperAdmin && {
      key: "visa-expiring",
      label: "Visa Expiring Clients",
      onClick: () => setOpenVisaExpiringStudentsModal(true),
    },
    isAdminOrSuperAdmin && {
      key: "archived",
      label: "Archived Clients",
      onClick: () => navigate("/admin/client/archived"),
    },
  ].filter(Boolean);

  const filtersToolbar = (
    <div className="px-3 sm:px-4 pb-2">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:flex-wrap">
        <div className="flex items-center gap-2 flex-wrap w-full sm:w-auto">
          <Button
            variant="outlined"
            size="small"
            startIcon={
              <Badge color="primary" variant="dot" invisible={activeFilterChips.length === 0}>
                <FilterList fontSize="small" />
              </Badge>
            }
            onClick={(e) => setFilterMenuAnchor(e.currentTarget)}
          >
            Filters
          </Button>
          <Menu
            anchorEl={filterMenuAnchor}
            open={Boolean(filterMenuAnchor)}
            onClose={() => setFilterMenuAnchor(null)}
          >
            {availableFilters.map((f) => (
              <MenuItem key={f.key} onClick={() => toggleFilter(f.key)} dense>
                <Checkbox
                  edge="start"
                  size="small"
                  checked={visibleFilters.has(f.key)}
                  tabIndex={-1}
                  disableRipple
                />
                <ListItemText primary={f.label} />
              </MenuItem>
            ))}
          </Menu>
          <Button
            onClick={clearFilters}
            variant="outlined"
            size="small"
            startIcon={<FaTimes />}
            disabled={activeFilterChips.length === 0 && !pagination?.searchText}
          >
            Clear
          </Button>
        </div>

        <TextField
          size="small"
          fullWidth
          className="sm:max-w-md sm:flex-1"
          placeholder="Search clients..."
          value={pagination?.searchText || ""}
          onChange={(e) =>
            setPagination((prev) => ({ ...prev, searchText: e.target.value, pageIndex: 0 }))
          }
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" className="text-app-text-muted" />
              </InputAdornment>
            ),
            endAdornment: pagination?.searchText ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() =>
                    setPagination((prev) => ({ ...prev, searchText: "", pageIndex: 0 }))
                  }
                  aria-label="Clear search"
                >
                  <Close fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
      </div>

      <div className="mt-2 flex flex-col sm:flex-row sm:flex-wrap gap-2">
        <Autocomplete
          options={sortByOptions}
          disableClearable
          getOptionLabel={(option) => option.label || ""}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          value={
            sortByOptions.find((option) => option.value === pagination?.sortBy) || sortByOptions[0]
          }
          onChange={(event, newValue) => {
            handleFilterChange("sortBy", newValue ? newValue.value : DEFAULT_CLIENT_SORT_BY);
          }}
          renderInput={(params) => <TextField {...params} label="Sort By" size="small" />}
          sx={filterFieldSx}
        />
        <Autocomplete
          options={sortOrderOptions}
          disableClearable
          getOptionLabel={(option) => option.label || ""}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          value={
            sortOrderOptions.find((option) => option.value === pagination?.sortDirection) ||
            sortOrderOptions[0]
          }
          onChange={(event, newValue) => {
            handleFilterChange(
              "sortDirection",
              newValue ? newValue.value : DEFAULT_CLIENT_SORT_DIRECTION
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Order"
              size="small"
              helperText={
                pagination?.sortBy === "visaExpiry"
                  ? pagination?.sortDirection === "DESC"
                    ? "Latest expiry first"
                    : "Soonest upcoming first"
                  : " "
              }
            />
          )}
          sx={filterFieldSx}
        />
        {availableFilters.filter((f) => visibleFilters.has(f.key)).map((f) => f.render())}
      </div>

      {activeFilterChips.length > 0 && (
        <div className="mt-2.5 flex flex-wrap items-center gap-2">
          <span className="text-xs font-semibold uppercase tracking-wide text-app-text-muted">
            Active:
          </span>
          {activeFilterChips.map((chip) => (
            <span
              key={chip.key}
              className="inline-flex items-center gap-1 rounded-full border border-app-border bg-app-surface px-2.5 py-1 text-xs text-app-text"
            >
              {chip.label}
              <button
                type="button"
                onClick={() => clearSingleFilter(chip.key)}
                className="text-app-text-muted hover:text-app-text"
                aria-label={`Remove ${chip.label}`}
              >
                <FaTimes className="text-[10px]" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="flex flex-wrap mt-4 dashBody bg-app-surface">
      <div className="w-full px-3 sm:px-4">
        <div className="relative flex flex-col min-w-0 break-words w-full shadow-lg rounded">
          <div className="rounded-t mb-0 px-3 sm:px-4 py-3 border-0">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <h3 className="font-semibold text-base sm:text-lg text-app-text m-0 truncate">
                  Clients ({paginatedClientList?.totalItems ?? 0})
                </h3>
                <RefreshData refreshData={() => refetchPaginatedClient()} />
              </div>
              <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap justify-end">
                <div className="hidden lg:flex items-center gap-2">
                  {headerSecondaryActions
                    .filter((a) => a.key !== "archived")
                    .map((action) => (
                      <Button
                        key={action.key}
                        variant="outlined"
                        size="small"
                        onClick={action.onClick}
                      >
                        {action.label}
                      </Button>
                    ))}
                </div>
                {/* <DownloadFile type="client" disabled={isPaginatedClientLoading} /> */}
                {hasPermission(P.CLIENTS.CREATE) && (
                  <Tooltip title="Add Client">
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<FaPlusCircle />}
                      component={Link}
                      to="/admin/client/add"
                      aria-label="Add client"
                    />
                  </Tooltip>
                )}
                {headerSecondaryActions.length > 0 && (
                  <>
                    <Tooltip title="More actions">
                      <IconButton
                        size="small"
                        onClick={(e) => setHeaderMoreAnchor(e.currentTarget)}
                        aria-label="More client actions"
                      >
                        {isCompact ? <MoreHoriz /> : <MoreVertIcon />}
                      </IconButton>
                    </Tooltip>
                    <Menu
                      anchorEl={headerMoreAnchor}
                      open={Boolean(headerMoreAnchor)}
                      onClose={() => setHeaderMoreAnchor(null)}
                      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                      transformOrigin={{ vertical: "top", horizontal: "right" }}
                    >
                      {(isCompact
                        ? headerSecondaryActions
                        : headerSecondaryActions.filter((a) => a.key === "archived")
                      ).map((action) => (
                        <MenuItem
                          key={action.key}
                          onClick={() => {
                            setHeaderMoreAnchor(null);
                            action.onClick();
                          }}
                        >
                          <ListItemText>{action.label}</ListItemText>
                        </MenuItem>
                      ))}
                    </Menu>
                  </>
                )}
              </div>
            </div>
          </div>

          {filtersToolbar}

          <div className="w-full min-w-0 overflow-hidden">
            {loading && <WholeScreenLoader open={loading} />}
            <div className="w-full overflow-x-auto">
              <MaterialReactTable table={table} />
            </div>
          </div>
        </div>
      </div>
      {openConfirmationModal.state && (
        <DeleteModal
          open={openConfirmationModal.state}
          item="Client"
          preview={permanentDelete ? deletePreview : null}
          previewLoading={permanentDelete ? deletePreviewLoading : false}
          bodyText={
            permanentDelete
              ? "This will permanently delete this client and everything tied to it — admissions, visa/insurance/skill-assessment/RPL records, tasks, appointments, and more. This cannot be undone."
              : "This will archive the client. It won't be permanently deleted and can be restored (or permanently deleted later) from the Archived Clients list."
          }
          extraContent={
            isSuperAdminUser && (
              <label className="flex items-center gap-2 mt-2 text-sm text-app-text-secondary">
                <input
                  type="checkbox"
                  checked={permanentDelete}
                  onChange={(e) => handlePermanentDeleteToggle(e.target.checked)}
                />
                Permanently delete instead — cannot be undone
              </label>
            )
          }
          handleCancel={() => {
            setOpenConfirmationModal({ state: false, id: null });
            setPermanentDelete(false);
            setDeletePreview(null);
          }}
          handleDelete={() =>
            permanentDelete ? handlePermanentDeleteClient() : handleDeleteClient()
          }
        />
      )}
      {isAssignModalOpen?.state && (
        <TaskModal
          open={isAssignModalOpen?.state}
          modalData={isAssignModalOpen?.data}
          handleClose={() => setIsAssignModelOpen({ state: false, data: null })}
          module={"Client"}
          modal={true}
        />
      )}
      {previewModalOpen && (
        <PreivewModal
          open={previewModalOpen}
          data={previewData}
          handleClose={() => setPreviewModalOpen(false)}
          isFetching={isPreviewFetching}
          fetchSelectedClientData={fetchSelectedClientData}
        />
      )}
      {importModalOpen && (
        <ImportModal
          open={importModalOpen}
          handleClose={() => setImportModalOpen(false)}
          onImportAll={handleImportAll}
          onImportSelected={handleImportSelected}
          onImportUpdated={handleImportUpdated}
          isLoading={loading || isPreviewFetching}
        />
      )}
      <ClientDetailsShareModal
        open={sharePrForm}
        initialForm="pr"
        onClose={() => setSharePrForm(false)}
      />
      {openVisaExpiringStudentsModal && (
        <VisaExpiringStudentsModal
          open={openVisaExpiringStudentsModal}
          handleClose={() => setOpenVisaExpiringStudentsModal(false)}
        />
      )}
    </div>
  );
};

export default Clients;
